import {
  CONVERT_LINE_FIELDS,
  CONVERT_LINE_PATH_PATTERN,
  convertLineChanges,
  convertLineEditOf,
  convertLineNotesWrite,
  type ConvertLineEdit,
  type ConvertLineField,
} from '@/lib/deal-convert-lines';
import type { QuoteProduct } from '@/lib/types';

/*
 * ═══════════════════════════════════════════════════════════════════════════
 * Editing a quote's line items ON THE WAY INTO AN INVOICE.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `POST /quotes/{id}/convert` copies every line of a quote onto a new Draft
 * invoice — with its project, its city set, its region set and its campaign
 * inclusions — inside the transaction that creates it.
 *
 * ⚠️ **IT NOW TAKES ONE OPTIONAL KEY, `lines`.** *SUPERSEDED, and it was
 * asserted in four places in this repo: "the endpoint's body schema is
 * `z.object({}).strict()` — it takes no line overrides at all", so "an editable
 * grid here could only ever pretend".* That was true when written and is not
 * now: `convertQuoteSchema` is `z.object({ lines: quoteLineOverridesArray
 * .optional() }).strict()`, measured in the backend repo on 2026-08-31.
 *
 * ⚠️ **`lines` RESPELLS COPIED LINES. IT CANNOT ADD ONE AND CANNOT CHOOSE WHICH
 * ARE BILLED.** Every line of the quote is billed on every call whether or not
 * the key is sent. That is the property the backend schema's own comment exists
 * to protect: `quoteProductIds` — which WOULD choose — is deliberately kept off
 * this endpoint and lives on `POST /quotes/{id}/invoices`, because "bill part of
 * this quote" and "bill all of it" one forgotten field apart is a wrong default
 * on a document that demands money. **Do not read this module as precedent for
 * folding the two endpoints together.**
 *
 * ## ⚠️ ONE GRAMMAR, TWO ROUTES — and this module deliberately owns almost none of it
 *
 * The shape is the deal one with `quoteProductId` in place of `dealLineId`; the
 * backend schema calls them "two paths, one grammar". So the seed, the change
 * test, the validator and — above all — the DISCOUNT PAIR are imported from
 * `@/lib/deal-convert-lines` rather than restated here. That module's header
 * says what a second copy costs: two implementations of one money rule agree on
 * the day they are written and drift on the first correction, and what would
 * drift is the pair whose halves are mutually exclusive columns
 * (`ck_f_invoice_products_discount_exclusive`) on a customer's invoice.
 *
 * What is genuinely this module's own is the ID KEY and nothing else.
 *
 * ## ⚠️ THERE IS NO `taxRate` AND NO `xeroTaxType`, AND NEITHER MAY BE ADDED
 *
 * The backend schema carries an explicit prohibition on both, quoted here
 * because it is the one thing a reader is most likely to "finish": a converted
 * line's tax is the QUOTE's own, `copyLinesToInvoice` carries `tax_rate` and
 * `xero_tax_type` across AS A PAIR, and a per-line tax key would be a second
 * writer on the figure the customer already agreed to. The body is `.strict()`,
 * so sending one is a 422 — and a control offering a value the request cannot
 * carry is a silent discard, which is why the grid's Tax cell is a READOUT.
 */

/** One line's override, exactly as the wire carries it. */
export interface QuoteConvertLineOverride {
  /**
   * ⚠️ **`f_quote_products.id` — the LINE's id, never the quote's.** It must be
   * one of that quote's own lines; anything else is a 422 at
   * `lines.<n>.quoteProductId`, and so is the same id twice.
   */
  quoteProductId: number;
  quantity?: number;
  unitPrice?: string;
  /** A percentage, as typed. Travels with `discountAmount`, always. */
  discount?: string;
  /** An exact sum, or `null` for "this line discounts by percentage". */
  discountAmount?: string | null;
  /** Verbatim wording, or `null` to go back to DERIVING it server-side. */
  description?: string | null;
  /**
   * Internal line note (owner 2026-10-18); absent copies the quote line's,
   * `null` clears it. Never sent to Xero. Built by `convertLineNotesWrite`.
   */
  notes?: string | null;
}

/**
 * The body of `POST /quotes/{id}/convert`.
 *
 * ⚠️ **`{}`, never an absent body.** The schema is `.strict()` and still expects
 * a body; `request()` omits `Content-Type` when there is none, which is exactly
 * what once 422'd every Convert click in production.
 *
 * ⚠️ **OMIT `lines` ENTIRELY WHEN NOTHING WAS EDITED**, and note this is NOT the
 * clearing `[]` of `cityIds` or `inclusions`: `lines: []` means "respell none",
 * not "bill no lines". An absent key is byte-for-byte the behaviour every
 * conversion had before overrides existed, which is the property the backend's
 * own first test asserts. {@link quoteConvertLineOverrides} returns `[]` and the
 * caller omits the key on a length of zero, so the two can never be confused.
 */
export interface QuoteConvertInput {
  lines?: QuoteConvertLineOverride[];
}

/** Re-exported so a caller needs one import for the whole grammar. */
export type { ConvertLineEdit, ConvertLineField };
export { convertLineEditOf as quoteConvertLineEditOf };

/**
 * One line's override, or `null` when nothing on that row moved.
 *
 * One line over {@link convertLineChanges}, which is where the discount pair
 * lives. Everything that decides WHAT is sent is shared with the deal path; the
 * only thing decided here is which id key it is stamped with.
 */
export function quoteConvertLineOverrideOf(
  quoteProductId: number,
  edit: ConvertLineEdit,
  seed: ConvertLineEdit,
): QuoteConvertLineOverride | null {
  const changes = convertLineChanges(edit, seed);
  return changes === null ? null : { quoteProductId, ...changes };
}

/**
 * Every changed row, in the order the quote's lines were read.
 *
 * ⚠️ **The INDEX of an entry is what a server refusal names** —
 * `lines.<n>.quantity` — so the caller keeps this order to route a 422 back onto
 * the row that caused it. Returning the array rather than a map is what makes
 * that index a fact rather than a guess.
 */
export function quoteConvertLineOverrides(
  lines: readonly QuoteProduct[],
  edits: Readonly<Record<number, ConvertLineEdit>>,
  /**
   * The row's note as it stands, by quote line id. A line absent here sends no
   * `notes`; one present sends it only when it differs from the quote line's
   * own (see `convertLineNotesWrite`). A note-only edit is an override of its own.
   */
  notes: Readonly<Record<number, string | null>> = {},
): QuoteConvertLineOverride[] {
  const overrides: QuoteConvertLineOverride[] = [];
  for (const line of lines) {
    const edit = edits[line.id];
    if (edit === undefined) continue;
    const override = quoteConvertLineOverrideOf(line.id, edit, convertLineEditOf(line));
    const draftNote = notes[line.id];
    const note =
      draftNote === undefined ? undefined : convertLineNotesWrite(draftNote, line.notes);
    if (note !== undefined) {
      overrides.push({ ...(override ?? { quoteProductId: line.id }), notes: note });
    } else if (override !== null) {
      overrides.push(override);
    }
  }
  return overrides;
}

/**
 * A server refusal, put back on the control that caused it.
 *
 * ⚠️ **The array INDEX is the only link between a refusal and a row.** The
 * server names `lines.2.quantity`, and `2` is a position in the array that was
 * SENT — not a `quoteProductId`, and not a position in the quote's own line
 * list, which is longer because unchanged rows are omitted. So the same
 * `overrides` array that was sent has to be handed back here; anything else
 * silently marks the wrong row, with a red box on a figure nobody touched.
 *
 * ⚠️ **Anything that does not bind to a control comes back as `unrouted`, to be
 * BANNERED rather than dropped** — a refusal on `quoteProductId`, which is ours
 * to get right and has no box on any screen; an index the array does not have;
 * or a field name this branch has not been taught about.
 */
export function quoteConvertLineFieldErrors(
  fieldErrors: Readonly<Record<string, string>>,
  overrides: readonly QuoteConvertLineOverride[],
): {
  rows: Record<number, Partial<Record<ConvertLineField, string>>>;
  unrouted: string[];
} {
  const rows: Record<number, Partial<Record<ConvertLineField, string>>> = {};
  const unrouted: string[] = [];

  for (const [path, message] of Object.entries(fieldErrors)) {
    const match = CONVERT_LINE_PATH_PATTERN.exec(path);
    if (match === null) continue;

    const override = overrides[Number(match[1])];
    const field = match[2];
    if (
      override === undefined ||
      field === undefined ||
      !CONVERT_LINE_FIELDS.includes(field as ConvertLineField)
    ) {
      unrouted.push(message);
      continue;
    }

    const row = rows[override.quoteProductId] ?? {};
    row[field as ConvertLineField] = message;
    rows[override.quoteProductId] = row;
  }

  return { rows, unrouted };
}
